import React, { useState, useEffect, useMemo } from "react";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { tTax } from "../utils/taxonomyI18n";

export type CatalogCategory = { category: string; items: string[] };

type CatalogPickerProps = {
  type: "passion" | "power";
  mode: "single" | "multiple";
  selected: string[];
  onSelect: (label: string) => void;
  onClose?: () => void;
};

// Sélecteur de passions/powers adossé au catalogue partagé (DB), qui a remplacé les listes
// statiques dupliquées entre web/mobile/backend. Reprend le pattern grouped/idle-vs-flat-search
// de CategoryDropdown (Create.tsx) : groupé par catégorie tant qu'on ne tape rien, liste plate
// filtrée dès la première frappe. Toujours visible en bas : proposer une entrée manquante.
export default function CatalogPicker({ type, mode, selected, onSelect, onClose }: CatalogPickerProps) {
  const { lang } = useApp();
  const [categories, setCategories] = useState<CatalogCategory[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [showPropose, setShowPropose] = useState(false);
  const [proposeCategory, setProposeCategory] = useState("");
  const [proposeLabel, setProposeLabel] = useState("");
  const [proposeSubmitting, setProposeSubmitting] = useState(false);
  const [proposeError, setProposeError] = useState("");
  const [proposeSuccess, setProposeSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCategories(null);
    setLoadError(false);
    api.get(`/catalog?type=${type}`)
      .then(data => { if (!cancelled) setCategories(data?.categories || []); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [type]);

  const flatFiltered = useMemo(() => {
    if (!categories) return [];
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const cat of categories) {
      for (const item of cat.items) {
        if (item.toLowerCase().includes(q) && !seen.has(item)) {
          seen.add(item);
          out.push(item);
        }
      }
    }
    return out;
  }, [categories, search]);

  const noun = type === "passion" ? "passion" : "power";
  const indefiniteArticle = type === "passion" ? "une" : "un";
  const proposeCta = type === "passion" ? "Proposez-la" : "Proposez-le";
  const proposeArticle = `${indefiniteArticle} ${noun}`;

  const row = (label: string) => {
    const isSelected = selected.includes(label);
    const disabled = mode === "multiple" && isSelected;
    return (
      <div
        key={label}
        onClick={() => { if (!disabled) onSelect(label); }}
        style={{
          padding: "11px 14px", cursor: disabled ? "default" : "pointer", fontSize: 14,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          backgroundColor: isSelected ? "#F0F0F0" : "transparent",
          color: disabled ? "#999" : "#0a0a0a",
          fontWeight: isSelected ? 600 : 400,
        }}
      >
        {tTax(lang, label)}
        {isSelected && <span style={{ color: "#0a0a0a", fontWeight: 700 }}>✓</span>}
      </div>
    );
  };

  const handlePropose = async () => {
    if (!proposeLabel.trim() || proposeSubmitting) return;
    setProposeSubmitting(true);
    setProposeError("");
    try {
      const data = await api.post("/catalog/propose", {
        type,
        category: proposeCategory.trim() || undefined,
        label: proposeLabel.trim(),
      });
      if (data?.proposal) {
        setProposeSuccess(true);
        setTimeout(() => {
          setShowPropose(false);
          setProposeSuccess(false);
          setProposeLabel("");
          setProposeCategory("");
        }, 2200);
      } else {
        setProposeError(data?.error || "Erreur lors de l'envoi de la proposition");
      }
    } catch {
      setProposeError("Erreur de connexion");
    } finally {
      setProposeSubmitting(false);
    }
  };

  const datalistId = `catalog-propose-categories-${type}`;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid #F0F0F0" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`Rechercher ${indefiniteArticle} ${noun}...`}
          autoFocus
          style={{ border: "none", outline: "none", fontSize: 14, flex: 1, backgroundColor: "transparent" }}
        />
        {onClose && (
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: "#999", lineHeight: 1, flexShrink: 0 }}>✕</button>
        )}
      </div>

      <div style={{ overflowY: "auto", maxHeight: 320 }}>
        {categories === null && !loadError && (
          <div style={{ padding: "20px 14px", textAlign: "center", color: "#999", fontSize: 13 }}>Chargement...</div>
        )}
        {loadError && (
          <div style={{ padding: "20px 14px", textAlign: "center", color: "#999", fontSize: 13 }}>Impossible de charger le catalogue.</div>
        )}
        {categories && !search && categories.map(cat => (
          <div key={cat.category}>
            <div style={{ position: "sticky", top: 0, padding: "7px 14px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" as const, color: "#999", backgroundColor: "#FAFAFA", borderBottom: "1px solid #F5F5F5" }}>
              {tTax(lang, cat.category)}
            </div>
            {cat.items.map(row)}
          </div>
        ))}
        {categories && search && (
          flatFiltered.length > 0 ? flatFiltered.map(row) : (
            <div style={{ padding: "20px 14px", textAlign: "center", color: "#999", fontSize: 13 }}>Aucun résultat pour "{search}"</div>
          )
        )}
      </div>

      <div style={{ borderTop: "1px solid #F0F0F0", padding: "12px 14px", flexShrink: 0 }}>
        {!showPropose ? (
          <div onClick={() => setShowPropose(true)} style={{ fontSize: 13, color: "#0a0a0a", fontWeight: 600, cursor: "pointer", textAlign: "center" }}>
            Vous ne trouvez pas votre {noun} ? {proposeCta}
          </div>
        ) : proposeSuccess ? (
          <div style={{ fontSize: 13, color: "#0a0a0a", textAlign: "center", fontWeight: 600 }}>
            Merci ! Ta proposition a été envoyée à l'équipe OLTODO.
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#666" }}>Proposer {proposeArticle}</span>
              <button onClick={() => { setShowPropose(false); setProposeError(""); }} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 12, color: "#999" }}>Annuler</button>
            </div>
            <input
              value={proposeLabel}
              onChange={e => setProposeLabel(e.target.value)}
              placeholder={type === "passion" ? "Ex: Padel, Escalade..." : "Ex: Cours de guitare..."}
              maxLength={60}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 13.5, outline: "none", marginBottom: 8, boxSizing: "border-box" }}
            />
            <input
              value={proposeCategory}
              onChange={e => setProposeCategory(e.target.value)}
              list={datalistId}
              placeholder="Catégorie (optionnel)"
              maxLength={40}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 13.5, outline: "none", marginBottom: 8, boxSizing: "border-box" }}
            />
            <datalist id={datalistId}>
              {(categories || []).map(c => <option key={c.category} value={c.category} />)}
            </datalist>
            {proposeError && <div style={{ fontSize: 12, color: "#c0392b", marginBottom: 8 }}>{proposeError}</div>}
            <button
              onClick={handlePropose}
              disabled={!proposeLabel.trim() || proposeSubmitting}
              style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", backgroundColor: proposeLabel.trim() ? "#0a0a0a" : "#E0E0E0", color: "white", fontSize: 13, fontWeight: 700, cursor: proposeLabel.trim() ? "pointer" : "not-allowed" }}
            >
              {proposeSubmitting ? "Envoi..." : "Envoyer la proposition"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
